import { Empty } from "@bednbody/ui/components/empty";
import { Badge } from "@bednbody/ui/components/badge";
import { Skeleton } from "@bednbody/ui/components/skeleton";
import { ArrowRightIcon, MapPinIcon } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { minutesSince } from "@/lib/time";
import type { useOngoingTrips } from "../command-center.hooks";

type Trips = NonNullable<ReturnType<typeof useOngoingTrips>["data"]>;

/**
 * Trips currently on the road, fronting /live-map. Live GPS rendering stays
 * on the map page: it polls `ops_live_trips_snapshot()` (0086) and draws with
 * mapbox-gl, which we deliberately keep out of the dashboard bundle
 * (App.tsx lazy-loads it for the same reason). The `trips:live` Broadcast this
 * used to name never had a publisher and is gone — OQ-99.
 */
export function OngoingTripsCard({
  trips,
  isLoading,
  canOpenMap,
}: {
  trips: Trips;
  isLoading: boolean;
  /** false for roles without /live-map access (executive). */
  canOpenMap: boolean;
}) {
  if (isLoading) {
    return (
      <div className="space-y-2">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-12 motion-reduce:animate-none" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {trips.length === 0 ? (
        <Empty>No trips on the road right now.</Empty>
      ) : (
        <ul className="max-h-64 space-y-2 overflow-y-auto pr-1">
          {trips.map((t) => (
            <li
              key={t.id}
              className="flex items-center justify-between gap-3 rounded-xl border border-border bg-card px-3 py-2"
            >
              <div className="flex min-w-0 items-center gap-2.5">
                <span className="relative flex size-2.5 shrink-0">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-info opacity-60" />
                  <span className="relative inline-flex size-2.5 rounded-full bg-info" />
                </span>
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-foreground">
                    {t.staff?.full_name ?? "Rider"}
                  </p>
                  <p className="truncate text-xs text-muted-foreground">
                    {t.bookings?.ref ?? ""} ·{" "}
                    {t.started_at ? `${minutesSince(t.started_at)}m on the road` : "just started"}
                  </p>
                </div>
              </div>
              <Badge variant="info-soft" shape="soft" className="shrink-0 capitalize">
                {t.bookings?.status?.replace(/_/g, " ") ?? "en route"}
              </Badge>
            </li>
          ))}
        </ul>
      )}

      {canOpenMap && (
        <Link
          to="/live-map"
          className="flex items-center justify-center gap-1.5 rounded-xl bg-primary/10 px-3 py-2.5 text-sm font-medium text-primary-dark transition-colors hover:bg-primary/15 dark:text-primary-light"
        >
          <MapPinIcon size={15} /> Open live map <ArrowRightIcon size={14} />
        </Link>
      )}
    </div>
  );
}
